// src/prototype/data/adapters/useCaseContentTypes.ts
// ============================================================
// USE-CASE CONTENT ADAPTER — TYPES
//
// Phase 3 (Full-Content Drilldown v4). Two type families:
//
//   1. BQ ROW types — mirror the BigQuery target schemas documented in
//      docs/dva-bigquery-data-model.json (client_use_case_scores, measure_results,
//      client_measure_rationalizations, uc_alignment_items, uc_impl_requirements,
//      uc_impl_milestones, uc_risks, uc_roi_assumptions_v2, financial_projections_v2,
//      uc_roi_benchmarks_v2, uc_roi_calculations). Field NAMES and TYPES match the
//      warehouse so the mock row source (useCaseContentMock.ts) can be swapped for a
//      live fetch without touching the UI.
//
//   2. VIEW-MODEL types — the shapes the v4 hub tabs actually render. The adapter
//      (useCaseContentAdapter.ts) is the ONLY place that maps BQ rows → view models,
//      so live data replacing the mocks never reaches a component.
//
// Anchor: dva-stage / client_id = "300" (supersedes the cs-poc sandbox / client 200).
// opp-1 = use_case_id 1555 "Real-Time Patient Flow and Capacity Management" (composite
// 82.0, confidence 0.61 display constant — stage confidence_level is NULL for client 300).
// Featured content is served from stage300FeaturedContent.ts. See docs/dva-core-population-changelog.md.
// ============================================================

// ---- shared enums / literals (match warehouse spellings) --------------------

/** The four scoring dimensions (P2). id is stable; used as a join key. */
export type DimensionId = 'financial' | 'urgency' | 'strategic' | 'feasibility';

/** evidence_json[].type — provenance kind behind a measure score or ROI assumption. */
export type EvidenceType =
  | 'survey'
  | 'document'
  | 'benchmark'
  | 'calculation'
  | 'assumption'
  | 'inference';

/** uc_roi_assumptions_v2.role — the assumption's part in the ROI calculation. */
export type AssumptionRole = 'driver' | 'cost' | 'rate';

/** uc_risks.severity */
export type RiskSeverity = 'Critical' | 'High' | 'Medium' | 'Low';

/** uc_impl_requirements.req_type */
export type RequirementType = 'data' | 'technology' | 'people' | 'governance';

// =============================================================================
// 1. BigQuery ROW types (target schema — see docs/dva-bigquery-data-model.json)
// =============================================================================

/** client_use_case_scores — one row per (client, use case). */
export interface ClientUseCaseScoreRow {
  client_id: string;
  use_case_id: number;
  value_score: number;
  implementation_score: number;
  total_score: number;
  rank: number;
  /**
   * 0–1, or NULL. dva-stage client_use_case_scores.confidence_level IS NULL for client 300,
   * so the featured path now carries null rather than a fabricated display constant. The UI
   * renders the confidence pill only when this is a number.
   */
  confidence_level: number | null;
  financial_composite: number;
  mode: string;
  scoring_run_id: string;
}

/**
 * measure_results — one row per (client, use case, measure). In the warehouse the
 * dimension is resolved by joining measures→uc_categories; the mock carries it inline
 * (see note in useCaseContentMock.ts). evidence_json is stored as JSON in BQ.
 */
export interface MeasureResultRow {
  client_id: string;
  use_case_id: number;
  measure_id: string;
  dimension: DimensionId; // join-derived (measures.category_id → dimension)
  measure_name: string;
  /** Value on `score_scale`. Default 0–100; the stage-sourced CC path is 1–5 (never converted). */
  /** NULL when `evaluated` is false — a gap carries no score, never a substituted one. */
  raw_score: number | null;
  /**
   * False when the source row was rejected as not-an-evaluation. The measure still renders, with an
   * honest state instead of a score, and is excluded from its dimension's rollup. Absent = true.
   */
  evaluated?: boolean;
  /** Scale `raw_score` is expressed on. Absent = '0to100' (the historical default). */
  score_scale?: MeasureScoreScale;
  /** 0–1, or NULL where the warehouse has no confidence for the row. */
  confidence_level: number | null;
  evidence_json: EvidenceItemRow[];
  scoring_version: string;
}

/**
 * dva-stage stores per-measure scores on a 1–5 scale while the demo-authored dimension root
 * is 0–100. The two are NEVER reconciled by arithmetic — the scale travels with the value so
 * the UI can pick the right renderer (pips vs. a 0–100 chip).
 */
export type MeasureScoreScale = '0to100' | '1to5';

/** evidence_json array element. */
export interface EvidenceItemRow {
  type: EvidenceType;
  src: string;
  excerpt: string;
}

/** client_measure_rationalizations — the narrative behind each measure score. */
export interface MeasureRationalizationRow {
  client_id: string;
  use_case_id: number;
  measure_id: string;
  rationalization_text: string;
  score: number;
  /** client_measure_rationalizations.weight_label — present on the stage-sourced path. */
  weight_label?: 'High' | 'Medium' | 'Low';
  /** client_measure_rationalizations.source_type — e.g. 'llm', 'p4_delta'. */
  source_type?: string;
}

/** Row-level provenance for a stage-sourced rationale set (client_measure_rationalizations). */
export interface RationaleProvenanceRow {
  project: string;
  dataset: string;
  table: string;
  clientId: string;
  useCaseId: number;
  correlationId: string;
  contractVersion: string;
  extractedAt: string;
}

/** The p4 personalization delta — use-case level, its own contract_version. */
export interface PersonalizationDeltaRow {
  archetype: string;
  archetypeSlug: string;
  activePriorities: readonly string[];
  relevanceTier: string;
  intensityTier: string;
  bvDelta: number;
  easeDelta: number;
  deltaId: string;
  contractVersion: string;
  text: string;
}

/** client_priority_themes — a theme and how strongly it was detected. */
export interface PriorityThemeRow {
  slug: string;
  label: string;
  intensity: number;
  activeInDelta: boolean;
}

/** uc_alignment_items — structured strategic-alignment items (P2/P4). */
export interface UcAlignmentItemRow {
  client_id: string;
  use_case_id: number;
  priority: string;
  score: number; // 0–100
  narrative: string;
  method_note: string;
}

/** uc_impl_requirements — data / technology / people / governance requirements. */
export interface UcImplRequirementRow {
  client_id: string;
  use_case_id: number;
  req_type: RequirementType;
  name: string;
  details: string;
}

/** uc_impl_milestones — dependency-aware roadmap milestones. */
export interface UcImplMilestoneRow {
  client_id: string;
  use_case_id: number;
  label: string;
  months_from_start: number;
  value_note: string;
  depends_on: string | null;
}

/** uc_risks — the P1 risk register (category added vs the v3 shape). */
export interface UcRiskRow {
  client_id: string;
  use_case_id: number;
  risk_id: string;
  name: string;
  category: string;
  severity: RiskSeverity;
  likelihood: string;
  mitigation: string;
  owner: string;
  description: string;
}

/** uc_roi_assumptions_v2 — traceable ROI assumptions + rolled-up outputs. */
export interface UcRoiAssumptionRow {
  client_id: string;
  use_case_id: number;
  label: string;
  value: string; // pre-formatted display value (e.g. "$170M", "10%")
  unit: string;
  role: AssumptionRole;
  calc_note: string;
  confidence: number; // 0–1
  source: string;
  source_type: EvidenceType;
}

/** Rolled-up ROI outputs (uc_roi_assumptions_v2 output columns / P3). */
export interface UcRoiOutputsRow {
  client_id: string;
  use_case_id: number;
  annual_benefit_display: string; // e.g. "$11.8M – $22.7M"
  annual_benefit: number; // $M base case
  implementation_cost: number; // $M
  operating_cost: number; // $M/yr
  payback_months: number;
  npv_3yr: number; // $M
  calc_method: string;
  confidence: number; // 0–1
}

/** financial_projections_v2 — per-year projections ($M). */
export interface FinancialProjectionRow {
  client_id: string;
  use_case_id: number;
  year: number;
  projected_revenue: number; // $M
  projected_cost_savings: number; // $M
}

/** uc_roi_benchmarks_v2 — category benchmark bands (dimension table + client position). */
export interface UcRoiBenchmarkRow {
  category: string;
  min_annual_benefit: number;
  max_annual_benefit: number;
  min_improve: number;
  max_improve: number;
  min_addressable: number;
  max_addressable: number;
  // client position within the band (from uc_roi_assumptions_v2)
  client_lo: number;
  client_hi: number;
  client_improve: number;
  client_addressable: number;
}

/** uc_roi_calculations — the step-by-step derivation trace. */
export interface UcRoiCalculationRow {
  client_id: string;
  use_case_id: number;
  step: number;
  formula: string;
  inputs: string;
  result: string;
}

/** ROI scenario ranges (financial_projections_v2 scenario variants). */
export interface RoiScenarioRow {
  client_id: string;
  use_case_id: number;
  scenario_id: 'conservative' | 'base' | 'aggressive';
  name: string;
  range: string;
  npv_3yr: number;
  payback_months: number;
  confidence: number;
  note: string;
}

/**
 * What the mock/live source returns for one use case, keyed to BQ rows. Scoped to the
 * rows the v4 hub TABS consume (Brief + Scoring Rationale + the header/rail ROI summary).
 * The full ROI detail rows (UcRoiAssumptionRow, FinancialProjectionRow, UcRoiBenchmarkRow,
 * UcRoiCalculationRow, RoiScenarioRow) are typed above for when the ROI tab is migrated off
 * the reused EAROIModelPage (the phase-3 carve-out), but are not part of this bundle yet.
 */
export interface UseCaseContentRows {
  score: ClientUseCaseScoreRow;
  measures: MeasureResultRow[];
  rationalizations: MeasureRationalizationRow[];
  alignment: UcAlignmentItemRow[];
  requirements: UcImplRequirementRow[];
  milestones: UcImplMilestoneRow[];
  risks: UcRiskRow[];
  roiOutputs: UcRoiOutputsRow;
  /**
   * Optional authored per-dimension DISPLAY scores. When present, getScores uses these
   * for the dimension bar instead of the mean of the dimension's measures. Used by the
   * featured drilldown to show an illustrative 4-dimension breakdown derived from the real
   * value/implementation sub-scores + composite (client-300 per-measure data is provisional).
   * Purely a display concern — it never feeds scenarioModel's dimension arithmetic.
   */
  dimScores?: Partial<Record<DimensionId, number>>;
  /**
   * The dimension weights that PRODUCED `score.total_score`. Set only by a path that computes the
   * composite from them (the featured rollupComposite). Absent means the composite did not come
   * from a dimension-weight rollup, so no surface may claim a "% of composite" for a dimension.
   */
  compositeWeights?: Readonly<Record<DimensionId, number>>;
  /**
   * Present only on the stage-sourced path (Care Coordination / uc 1693). Drives the
   * page-level provenance line, the personalization delta card, and the theme chips.
   * Absent everywhere else, so no other use case's rendering changes.
   */
  provenance?: RationaleProvenanceRow;
  personalizationDelta?: PersonalizationDeltaRow;
  priorityThemes?: PriorityThemeRow[];
}

// =============================================================================
// 2. VIEW-MODEL types (what the v4 hub tabs render)
// =============================================================================

export interface EvidenceVM {
  type: EvidenceType;
  src: string;
  excerpt: string;
}

export interface MeasureVM {
  id: string;
  name: string;
  /** Value on `scale`; NULL for an unevaluated measure (renders as a gap, not a number). */
  raw: number | null;
  /** False → "Not yet evaluated for this use case": no pips, no weight, no source, no evidence. */
  evaluated: boolean;
  /** Which renderer the score gets: a 0–100 chip or 1–5 pips. */
  scale: MeasureScoreScale;
  /** 0–1, or null when the warehouse has no confidence for this measure. */
  conf: number | null;
  rat: string;
  ev: EvidenceVM[];
  /** Stage weight_label — renders as the weight chip. */
  weight?: 'High' | 'Medium' | 'Low';
  /** Stage source_type — renders as the source chip. */
  source?: string;
}

export interface DimensionVM {
  id: DimensionId;
  score: number; // 0–100
  measures: MeasureVM[];
  /** Evaluated vs total for THIS dimension. `evaluated < total` → the coverage footnote marker. */
  coverage: MeasureCoverageVM;
  /**
   * Where `score` came from.
   *  'measures'  — the mean of this dimension's EVALUATED measures.
   *  'authored'  — the demo-authored-v1 display score (DIM6). Featured drilldowns carry these so
   *                the composite and the Explorer ranking stay on one root; the bar is therefore
   *                NOT a rollup of the measures listed under it, and a gap cannot move it.
   */
  rollup: 'measures' | 'authored';
}

export interface ScoresVM {
  total: number; // composite
  /** 0–1, or null when the warehouse has no confidence (stage client 300). */
  confidence: number | null;
  asOf: string;
  run: string;
  dims: DimensionVM[];
  /** Weights that produced `total`; see UseCaseContentRows.compositeWeights. Undefined when none did. */
  compositeWeights?: Readonly<Record<DimensionId, number>>;
  /** Page-level measure coverage — drives the "N of 38 measures evaluated" header line. */
  coverage: MeasureCoverageVM;
  /** Stage-sourced path only — see UseCaseContentRows. */
  provenance?: RationaleProvenanceRow;
  personalizationDelta?: PersonalizationDeltaRow;
  priorityThemes?: PriorityThemeRow[];
}

export interface MeasureCoverageVM {
  /** Measures with a real evaluation behind them. */
  evaluated: number;
  /** Every measure on the page, evaluated or gap. */
  total: number;
}

export interface RiskVM {
  riskId: string;
  name: string;
  category: string;
  severity: RiskSeverity;
  likelihood: string;
  description: string;
  mitigation: string;
  owner: string;
}

export interface MilestoneVM {
  label: string;
  months: number;
  value: string;
  dependsOn: string | null;
}

export interface RequirementVM {
  type: RequirementType;
  name: string;
  details: string;
}

export interface AlignmentVM {
  priority: string;
  score: number;
  narrative: string;
  method: string;
}

/** Summary ROI outputs the hub header + Brief "Financial Model" card need. */
export interface RoiSummaryVM {
  outputs: {
    annualBenefit: string;
    annualBenefitBase: number;
    implCost: number;
    opCost: number;
    paybackMonths: number;
    npv3yr: number;
    calcMethod: string;
    confidence: number;
  };
}

// ---- dimension metadata (labels/descriptions/icons for the 4 dimensions) -----

export interface DimensionMeta {
  id: DimensionId;
  name: string;
  /** lucide icon key resolved in the tab components. */
  icon: string;
  desc: string;
}

/** The approved v4 4-dimension taxonomy. Replaces the page-local 5-dimension mock. */
export const DIM_META: DimensionMeta[] = [
  { id: 'financial', name: 'Financial Impact', icon: 'dollar', desc: 'Size and defensibility of the financial return, gated by the financial composite.' },
  { id: 'urgency', name: 'Operational Urgency', icon: 'clock', desc: 'How pressing the underlying operational problem is for your organization today.' },
  { id: 'strategic', name: 'Strategic Alignment', icon: 'target', desc: 'Fit with your stated priorities, board goals, and strategy documents.' },
  { id: 'feasibility', name: 'Implementation Feasibility', icon: 'zap', desc: 'Technical and organizational complexity relative to delivery capacity.' },
];

/** Scoring-run provenance shown in the "scores as of" marker across the app. */
export const SCORES_AS_OF = 'Jul 18, 2026 · 04:12 UTC';
export const SCORING_RUN = 'run_2026-07-18_0412';

/** CORE demo client — re-anchored to the dva-stage representative. client_id is ALWAYS a string. */
export const DEMO_CLIENT_ID = '300';
